import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import NotSpecifiedRoleDialog from '@/components/shared/Dialog/NotSpecifiedRoleDialog';
import EditPage from '@/components/shared/Layouts/EditPage/EditPage';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useUsersAutocomplete } from '@/hooks/autocomplete/useUsersAutocomplete';
import { useBudgetGetDistributorGroupInfo } from '@/hooks/useBudgetGetDistributorGroupInfo';
import { useDistributorGetDistributorGroupInfo } from '@/hooks/useDistributorGetDistributorGroupInfo';
import { useDistributorGroupUserMembership } from '@/hooks/useDistributorGroupUserMembership';
import type { Distributor } from '@/hooks/useDistributors';
import { useDistributors } from '@/hooks/useDistributors';
import { useManageDistributorGroupUsers } from '@/hooks/useManageDistributorGroupUsers';
import { usePendingRoleSelection } from '@/hooks/usePendingRoleSelection';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSwapDistributorRoleForDistributorGroup } from '@/hooks/useSwapDistributorRoleForDistributorGroup';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import { useUsersByDistributorGroup } from '@/hooks/useUsersByDistributorGroup';
import { ROUTES } from '@/routes';

interface LocationState {
  distributor?: Distributor;
  returnSearchParams?: string;
}

function EditDistributorList() {
  const { id } = useParams<{ id: string }>();
  const distributorGroupId = id ? parseInt(id, 10) : undefined;
  const { organizationId } = useOrganizationId();
  const location = useLocation();

  const snackbar = useSnackbar();

  const [debouncedValue, setDebouncedValue] = useState<string>('');
  const { markPending, resolvePending, isBlocked, proceed, reset } = usePendingRoleSelection<string>();

  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { distributors: distributorsGroup } = useDistributors(1, 50, 'DistributorName');

  const state = location.state as LocationState | undefined;
  const distributor =
    state?.distributor ??
    (distributorsGroup?.items && distributorGroupId
      ? (distributorsGroup.items.find((ag) => ag.systemId === distributorGroupId) ?? null)
      : null);

  const {
    data: usersGroupData,
    isLoading,
    isRefetching,
    error,
  } = useUsersByDistributorGroup(distributorGroupId, apiPage, pagination.pageSize);

  const { items: users, totalCount } = usersGroupData || {};

  const {
    addUsers,
    updateUser,
    removeUser,
    removeUsers,
    isMutating: isManagingUsers,
    removeUserMutation,
    removeUsersBatchMutation,
  } = useManageDistributorGroupUsers({ snackbar });

  const { getDistributorGroupInfo: getDistributorGroupDistributorInfo } = useDistributorGetDistributorGroupInfo();
  const { getDistributorGroupInfo: getBudgetDistributorGroupInfo } = useBudgetGetDistributorGroupInfo();

  const { swapDistributorRole, isSwappingRole } = useSwapDistributorRoleForDistributorGroup();

  const usersList =
    users?.map((u) => ({
      userId: u.userId ?? null,
      email: (u.email || u.userId) ?? null,
      fullName: u.fullName ?? null,
      role: u.role ?? '',
    })) ?? [];

  const usersAutocompleteQuery = useUsersAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: !isManagingUsers,
  });

  const usersIdsForAutocomplete =
    usersAutocompleteQuery.data?.map((u) => u.email).filter((email): email is string => email !== null && email !== undefined) ?? [];

  const membershipQuery = useDistributorGroupUserMembership({
    organizationId,
    groupId: distributorGroupId ?? 0,
    userIds: usersIdsForAutocomplete,
    enabled: !isManagingUsers && usersIdsForAutocomplete.length > 0,
  });

  const now = new Date();
  const currentDate = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${now.getFullYear()}`;

  return (
    <>
      <EditPage
        route={ROUTES.DISTRIBUTORS}
        group={distributor}
        organizationId={organizationId}
        isManagingUsers={isManagingUsers}
        pagination={{ state: pagination, onChange: setPagination }}
        breadcrumbItems={{
          prevPathLabel: 'Distributors',
          currentPathLabel: 'Manage Distributor',
          currentPath: `/distributors/manage/${distributorGroupId}`,
        }}
        sectionTitle={{
          testId: 'user-details-title',
          title: distributor?.distributorName || 'Distributor Name',
        }}
        autocompleteProps={{
          usersData: usersAutocompleteQuery,
          membershipData: membershipQuery,
          filter: debouncedValue,
          onFilteredSearch: setDebouncedValue,
          disabled: !distributorGroupId || isManagingUsers,
          onAddUsers: async (groupId, usersId, group) => {
            const distributor = group as Distributor | null;
            const result = await addUsers(groupId, usersId, {
              distributorName: distributor?.distributorName ?? null,
              distributorId: distributor?.distributorId ?? null,
              country: distributor?.country ?? null,
              state: distributor?.state ?? null,
              isActive: distributor?.isActive,
            });
            if (result) markPending(usersId);
            return result;
          },
        }}
        error={error}
        table={{
          testId: 'distributor-users-table',
          ariaLabel: 'Distributor users list',
          noDataMessage: 'No users assigned.',
          data: usersList,
          totalCount: totalCount ?? 0,
          onRemoveSingleUser: async (groupId, user) => {
            const result = await removeUser(groupId, user.userId!, user.role!);
            if (result) resolvePending([user.userId!]);
            return result;
          },
          onRemoveUsersInBatch: async (groupId, users) => {
            const result = await removeUsers(
              groupId,
              users.map((u) => ({ userId: u.userId!, role: u.role! })),
            );
            if (result) resolvePending(users.map((u) => u.userId!));
            return result;
          },
          isRefetching,
          isLoading,
          removeUserMutation,
          removeUsersBatchMutation,
          isSwappingRole,
          onUpdateRole: async (groupId, userId, newRole) => {
            const result = await updateUser(groupId, userId, newRole);
            if (result) resolvePending([userId]);
            return result;
          },
          getDistributorGroupConflict: (orgId, userId) => getDistributorGroupDistributorInfo(orgId, userId),
          getBudgetGroupConflict: (orgId, userId) => getBudgetDistributorGroupInfo(orgId, userId),
          onSwapConfirm: async (groupId, swapData) => {
            const result = await swapDistributorRole({
              organizationId,
              params: {
                userId: swapData.userId,
                currentDistributorGroupId: swapData.currentGroupId,
                newDistributorGroupId: groupId,
              },
            });
            if (result) resolvePending([swapData.userId]);
            return result;
          },
        }}
        exportConfig={{
          url: `/api/organizations/${organizationId}/distributor-groups/${distributorGroupId}/export-users`,
          fileName: `DG_${distributor?.distributorName}_Users_${currentDate}.xlsx`,
        }}
      />
      <NotSpecifiedRoleDialog open={isBlocked} onConfirm={proceed} onCancel={reset} />
    </>
  );
}

export default EditDistributorList;
